import { DeliveryChannel, ScheduledTask, TaskSchedule, TaskStatus } from '../../shared/services/scheduledTasksApi';

// 0=segunda .. 6=domingo (mesma convenção do backend, weekday do Python).
export const WEEKDAYS_PT = [
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
  'domingo',
];

/** Frase pt-BR do agendamento, ex.: "Diária às 08:00", "Semanal (sexta) às 09:00". */
export function describeSchedule(schedule: TaskSchedule): string {
  const time = schedule.time || '';
  switch (schedule.type) {
    case 'daily':
      return `Diária às ${time}`;
    case 'weekly': {
      const wd = typeof schedule.weekday === 'number' ? WEEKDAYS_PT[schedule.weekday] : '';
      return `Semanal (${wd}) às ${time}`;
    }
    case 'monthly':
      return `Mensal (dia ${schedule.day_of_month}) às ${time}`;
    case 'once':
      return `Uma vez em ${formatDate(schedule.date)} às ${time}`;
    default:
      return time;
  }
}

/** "15/07/2026" a partir de "2026-07-15". */
export function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/** Data + hora legível a partir de um ISO datetime (ex.: next_run_at).
 * O backend envia UTC com offset explícito; exibimos no fuso da TAREFA
 * (default America/Sao_Paulo) para bater com a linha da agenda ("Diária às
 * 16:50") — sem isso o navegador mostrava o horário UTC cru. Strings legadas
 * sem offset são tratadas como UTC. */
export function formatDateTime(iso?: string | null, timeZone?: string): string {
  if (!iso) return '—';
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const dt = new Date(hasOffset ? iso : iso + 'Z');
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timeZone || 'America/Sao_Paulo',
  });
}

/** Tempo relativo pt-BR a partir de agora: "em 22 horas", "há 2 horas", "agora".
 * Strings sem offset são tratadas como UTC (igual formatDateTime). */
export function relativeFromNow(iso?: string | null): string {
  if (!iso) return '';
  const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const t = new Date(hasOffset ? iso : iso + 'Z').getTime();
  if (isNaN(t)) return '';
  const diffMs = t - Date.now();
  const abs = Math.abs(diffMs);
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
  if (abs < min) return 'agora';
  if (abs < hour) return rtf.format(Math.round(diffMs / min), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (abs < 30 * day) return rtf.format(Math.round(diffMs / day), 'day');
  return rtf.format(Math.round(diffMs / (30 * day)), 'month');
}

/** true para agendamento de uma única vez (lembrete pontual). */
export function isOneTime(schedule: TaskSchedule): boolean {
  return schedule.type === 'once';
}

export interface StatusLabel {
  text: string;
  tone: 'active' | 'running' | 'paused' | 'done' | 'failed';
}

export function describeStatus(task: Pick<ScheduledTask, 'status'>): StatusLabel {
  const map: Record<TaskStatus, StatusLabel> = {
    active: { text: 'Ativa', tone: 'active' },
    running: { text: 'Executando', tone: 'running' },
    paused: { text: 'Pausada', tone: 'paused' },
    completed: { text: 'Concluída', tone: 'done' },
    failed_paused: { text: 'Pausada (falhas)', tone: 'failed' },
  };
  return map[task.status] || { text: task.status, tone: 'paused' };
}

/** Rótulo curto do resultado da última execução. */
export function describeRunStatus(status?: string | null): string {
  switch (status) {
    case 'success':
      return 'Sucesso';
    case 'error':
      return 'Erro';
    case 'timeout':
      return 'Tempo esgotado';
    case 'skipped':
      return 'Ignorada';
    case 'running':
      return 'Executando';
    default:
      return status || '—';
  }
}

/** Frase curta da agenda para o cartão: "Todo dia · 08:00", "Toda sexta · 09:00". */
export function shortSchedule(schedule: TaskSchedule): string {
  const time = schedule.time || '';
  switch (schedule.type) {
    case 'daily':
      return `Todo dia · ${time}`;
    case 'weekly': {
      const wd = typeof schedule.weekday === 'number' ? WEEKDAYS_PT[schedule.weekday] : '';
      // "sábado"/"domingo" são masculinos: "Todo sábado".
      const prefix = schedule.weekday === 5 || schedule.weekday === 6 ? 'Todo' : 'Toda';
      return `${prefix} ${wd} · ${time}`;
    }
    case 'monthly':
      return `Todo dia ${schedule.day_of_month} · ${time}`;
    case 'once':
      return `${formatDate(schedule.date)} · ${time}`;
    default:
      return time;
  }
}

export type DeliveryKind = 'in_app' | 'email' | 'slack';

/** Tipo e rótulo de um destino ("slack:time-eng" → Slack · time-eng). */
export function describeDelivery(entry: DeliveryChannel | string): { kind: DeliveryKind; label: string } {
  if (entry === 'email') return { kind: 'email', label: 'E-mail' };
  if (entry === 'slack') return { kind: 'slack', label: 'Slack' };
  if (entry.startsWith('slack:')) return { kind: 'slack', label: entry.slice('slack:'.length) };
  return { kind: 'in_app', label: 'Conversa' };
}

export type TaskGroup = 'active' | 'paused' | 'done';

/** Aba da página onde a tarefa aparece. */
export function taskGroup(task: Pick<ScheduledTask, 'status'>): TaskGroup {
  if (task.status === 'active' || task.status === 'running') return 'active';
  if (task.status === 'completed') return 'done';
  return 'paused';
}

/** Ordem da lista: próximas execuções primeiro; sem próxima, as mais recentes. */
export function compareTasks(a: ScheduledTask, b: ScheduledTask): number {
  const na = a.next_run_at ? Date.parse(a.next_run_at) : NaN;
  const nb = b.next_run_at ? Date.parse(b.next_run_at) : NaN;
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  if (!isNaN(na)) return -1;
  if (!isNaN(nb)) return 1;
  const la = Date.parse(a.last_run_at || a.created_at || '') || 0;
  const lb = Date.parse(b.last_run_at || b.created_at || '') || 0;
  return lb - la;
}
